/**
 * Zen Pacific Theme v3.1
 * Complete theme schema matching the JSON v3.1 specification
 *
 * Generated: 2026-03-25
 * WCAG Target: AAA (7:1 minimum for all text; 3:1 minimum for inactive/decorative)
 */

import { BrandThemeV3 } from '../types/theme.v3';

export const zenPacificThemeV3: BrandThemeV3 = {
  _meta: {
    version: "3.1",
    description: "Complete Cordiant theme schema — all transactional UI tokens, calendar, data display, WCAG AAA accessibility layer, plus status semantics, form states, modal/overlay, dropdown, link, radio, loading, tooltip, toggle, weather tokens, neutrals, celebrations, services, dining, auth, profile, workflows, events, and changeRequests. Backward-compatible with v2 and v3.0 schemas.",
    wcagTarget: "AAA (7:1 minimum for all text; 3:1 minimum for inactive/decorative)",
    generatedFor: "Zen Pacific Orlando (demo property)",
    changelog: {
      "v2→v3": "Added: status, forms.errorStates/disabledStates/successStates, forms.radio, modal, dropdown, link, loading, tooltip, toggle, weather. Extended: forms.checkbox (borderRadius). Deduplicated: content type CSS vars already in aiResponseSystem.contentTypes.",
      "v3→v3.1": "Added: neutrals (extended gray scale), celebrations (birthday/anniversary/wedding), services (lighting/AC/heater/TV/WiFi), dining (outlet theming), auth (sign-out with semantic names), profile (membership tiers), workflows (check-in/checkout/upgrade), events (business events), changeRequests. Moved cardBrands to globalConstants. Removed duplicate status colors from services (use status.* directly). Renamed auth.signOut properties to semantic names for portability."
    },
    globalConstants: {
      cardBrands: {
        visa: "#1434CB",        // Visa brand blue (immutable)
        mastercard: "#EB001B",  // Mastercard brand red (immutable)
        amex: "#006FCF",        // American Express blue (immutable)
        discover: "#FF6000"     // Discover orange (immutable)
      }
    },
    tokenResolutionGuide: {
      _description: "Token lookup order: domain-specific (celebrations/services) → foundational (status/brand) → never hardcode",
      order: [
        "1. Domain-specific tokens (celebrations.birthday.*, services.wifi.*, workflows.checkin.*)",
        "2. Foundational semantic tokens (status.error, status.success, brand.colors.primary)",
        "3. Component fallbacks (only if theme incomplete)",
        "4. Never hardcode colors directly in components"
      ],
      examples: {
        "Service success state": "Use status.success (not services.wifi.success)",
        "Service error state": "Use status.error (not services.ac.error)",
        "Payment card brands": "Use _meta.globalConstants.cardBrands.visa (immutable)",
        "Birthday decoration": "Use celebrations.birthday.primary (domain-specific)",
        "WiFi icon color": "Use services.wifi.primary (domain-specific visual identifier)"
      }
    }
  },

  brand: {
    name: "Zen Pacific",
    fonts: {
      primary: "Playfair Display, Georgia, 'Times New Roman', serif",
      secondary: "system-ui, -apple-system, 'Segoe UI', sans-serif"
    },
    colors: {
      cta: "#AC7F42",
      ctaText: "#6E5028",
      text: {
        light: "#545454",
        primary: "#1A1A1A",
        secondary: "#4A4A4A",
        disabled: "#736960"
      },
      primary: "#4C0D16",
      gradient: {
        end: "#1A1A1A",
        start: "#1A1A1A",
        middle: "#2D2D2D"
      },
      secondary: "#8B1538",
      surface: {
        base: "#FFFFFF",
        subtle: "#FAFAFA",
        warm: "#FDF6F0",
        hover: "#FAF6F0",
        border: "#E0E0E0",
        borderSubtle: "#F0F0F0"
      }
    },
    shadows: {
      light: "0 4px 12px rgba(76, 13, 22, 0.12)",
      medium: "0 8px 24px rgba(76, 13, 22, 0.15)"
    }
  },

  accessibility: {
    focusOutline: "#AC7F42",
    minTouchTarget: "44px",
    focusOutlineWidth: "3px",
    focusOutlineOffset: "2px",
    contrastRequirements: {
      normalText: 7.0,
      largeText: 4.5,
      inactiveComponents: 3.0,
      decorativeElements: "none"
    },
    _contrastAudit: {
      "text.primary on surface.base": { "ratio": 17.40, "level": "AAA" },
      "text.secondary on surface.base": { "ratio": 8.86, "level": "AAA" },
      "text.light on surface.base": { "ratio": 7.57, "level": "AAA" },
      "text.light on surface.subtle": { "ratio": 7.26, "level": "AAA" },
      "text.disabled on surface.base": { "ratio": 5.36, "level": "AA (inactive)" },
      "ctaText on surface.base": { "ratio": 7.39, "level": "AAA" },
      "white on cta (button)": { "ratio": 3.58, "level": "AA-Large — see note" },
      "white on primary (topbar)": { "ratio": 15.26, "level": "AAA" },
      "white on secondary": { "ratio": 9.28, "level": "AAA" },
      "primary on surface.warm": { "ratio": 14.25, "level": "AAA" },
      "_note_cta_button": "CTA gold #AC7F42 is 3.58:1 for white text. Acceptable for large bold button text (18px+/14px bold). For smaller text, use ctaText #6E5028 instead."
    }
  },

  status: {
    _description: "Functional semantic colors for error, warning, success, and info states. These are intentionally NOT brand-tinted — red means error, green means success, universally.",
    error: "#DC2626",
    errorLight: "#FCA5A5",
    errorBg: "#FEF2F2",
    warning: "#EA580C",
    warningLight: "#FDBA74",
    warningBg: "#FFF7ED",
    success: "#22C55E",
    successLight: "#86EFAC",
    successBg: "#F0FDF4",
    info: "#3B82F6",
    infoLight: "#93C5FD",
    infoBg: "#EFF6FF"
  },

  neutrals: {
    _description: "Extended neutral color palette — foundational grays, overlays, and dividers. Use these for consistent neutral tones across all components.",
    gray50: "#FAFAFA",
    gray100: "#F5F5F5",
    gray200: "#E5E5E5",
    gray300: "#D4D4D4",
    gray400: "#A3A3A3",
    gray500: "#737373",
    gray600: "#525252",
    gray700: "#404040",
    gray800: "#262626",
    gray900: "#171717",
    white: "#FFFFFF",
    black: "#000000",
    warmWhite: "#FFFEF9",
    offWhite: "#FAFAF8",
    cream: "#F5F5F0",
    charcoal: "#2A2A2A",
    slate: "#475569",
    steel: "#64748B",
    graphite: "#1E293B",
    overlay: {
      light: "rgba(0, 0, 0, 0.2)",
      medium: "rgba(0, 0, 0, 0.5)",
      heavy: "rgba(0, 0, 0, 0.8)"
    },
    divider: {
      subtle: "#F5F5F5",
      default: "#E5E5E5",
      strong: "#D4D4D4"
    },
    borderLight: "#F0F0F0",
    borderMedium: "#E0E0E0",
    borderDark: "#D4D4D4"
  },

  celebrations: {
    _description: "Celebration-specific color palettes for birthday, anniversary, and wedding workflows. These are intentionally vibrant and distinct from brand colors.",
    birthday: {
      primary: "#FF1493",      // DeepPink - vibrant hot pink for festive birthday vibes
      secondary: "#FFD700",    // Gold - rich, celebratory gold accent
      tertiary: "#FF69B4",     // HotPink - playful accent color
      light: "#FFF5F8",        // Very light pink tint
      lighter: "#FEF7F7",      // Even lighter pink for subtle backgrounds
      background: "#FFFBF0",   // Warm cream/ivory background
      border: "#FFED4E"        // Bright cheerful yellow border
    },
    anniversary: {
      primary: "#D63384",
      secondary: "#FCE7F3",
      tertiary: "#F9A8D4",
      light: "#FDF2F8",
      background: "#FEF2F2",
      border: "#FCA5A5",
      accent: "#F87171"
    },
    wedding: {
      primary: "#C2185B",
      secondary: "#D63384",
      light: "#FCE7F3",
      background: "#FAF5FF",
      border: "#D8B4FE",
      accent: "#C084FC"
    }
  },

  services: {
    _description: "Service-specific color palettes for technical support workflows (Lighting, AC, Heater, TV, WiFi). Each service has its own visual identity.",
    _note: "For success/error/warning/info states, use status.* tokens directly (one source of truth)",
    lighting: {
      primary: "#F59E0B",      // Amber - domain-specific visual identifier
      secondary: "#FCD34D",
      dark: "#D97706",
      darker: "#B45309",
      light: "#FDE68A",
      background: "#FFFBEB",
      border: "#FDE68A",
      icon: "#D97706",
      text: "#78350F"
    },
    ac: {
      primary: "#06B6D4",      // Cool blue - domain-specific visual identifier
      secondary: "#67E8F9",
      dark: "#0891B2",
      darker: "#0E7490",
      light: "#A5F3FC",
      background: "#ECFEFF",
      border: "#A5F3FC",
      icon: "#0891B2",
      text: "#164E63"
    },
    heater: {
      primary: "#EF4444",      // Warm red/orange - domain-specific visual identifier
      secondary: "#FCA5A5",
      dark: "#DC2626",
      darker: "#B91C1C",
      light: "#FECACA",
      background: "#FEF2F2",
      border: "#FECACA",
      icon: "#DC2626",
      text: "#7F1D1D"
    },
    tv: {
      primary: "#6366F1",      // Blue - domain-specific visual identifier
      secondary: "#A5B4FC",
      dark: "#4F46E5",
      darker: "#4338CA",
      light: "#C7D2FE",
      background: "#EEF2FF",
      border: "#C7D2FE",
      icon: "#4F46E5",
      text: "#312E81"
    },
    wifi: {
      primary: "#10B981",      // Green - domain-specific visual identifier
      secondary: "#6EE7B7",
      dark: "#059669",
      darker: "#047857",
      light: "#A7F3D0",
      background: "#ECFDF5",
      border: "#A7F3D0",
      icon: "#059669",
      text: "#064E3B"
    }
  },

  dining: {
    _description: "Dining outlet-specific theming. Allows per-outlet color customization while maintaining consistency.",
    outlets: {
      default: {
        brand: "#8B6F4E",
        brandLight: "#FAF9F7",
        accent: "#6F5A3E",
        accentLight: "#C4A783",
        background: "#2C3E50",
        surface: "#ECF0F1",
        text: "#2C3E50",
        border: "#D1C4B0"
      }
    }
  },

  auth: {
    _description: "Authentication flow theming — modal overrides, button variants, and sign-out palette.",
    modalOverrides: {
      background: "#FFFFFF",
      border: "#E0E0E0",
      shadow: "0 8px 24px rgba(76, 13, 22, 0.15)",
      titleColor: "#1A1A1A",
      bodyColor: "#4A4A4A",
      dividerColor: "#F0F0F0"
    },
    button: {
      primary: {
        background: "#4C0D16",
        text: "#FFFFFF",
        border: "1px solid #4C0D16",
        hoverBackground: "#8B1538"
      },
      secondary: {
        background: "#FFFFFF",
        text: "#4C0D16",
        border: "1px solid #4C0D16",
        hoverBackground: "#FAFAFA"
      },
      cancel: {
        background: "#FFFFFF",
        text: "#737373",
        border: "1px solid #E0E0E0",
        hoverBackground: "#F5F5F5"
      }
    },
    signOut: {
      _description: "Sign-out modal color palette with semantic, portable names",
      surfacePrimary: "#F6F3EE",      // Main surface/background (was: ivory)
      surfaceSecondary: "#EDE8E0",    // Secondary surface (was: ivoryWarm)
      brandAccent: "#5C2434",         // Primary brand accent (was: wine)
      decorativeAccent: "#C4A46C",    // Secondary decorative accent (was: gold)
      positiveAction: "#3D6B5E",      // Confirmation/continue action (was: sage)
      textPrimary: "#2A2522",         // Primary text color (was: charcoal)
      textSecondary: "#5A5550",       // Secondary text color
      border: "#EDE8E0"               // Border color (was: ivoryWarm)
    }
  },

  profile: {
    _description: "Profile management theming — cards, membership tiers, and status indicators.",
    card: {
      background: "#FFFFFF",
      border: "#E0E0E0",
      shadow: "0 4px 12px rgba(76, 13, 22, 0.12)",
      labelColor: "#737373",
      valueColor: "#1A1A1A"
    },
    membership: {
      gold: "#F59E0B",
      silver: "#9CA3AF",
      bronze: "#D97706",
      standard: "#525252"
    },
    status: {
      verified: "#22C55E",
      pending: "#F59E0B",
      incomplete: "#DC2626"
    }
  },

  workflows: {
    _description: "Workflow-specific theming for check-in, checkout, and upgrade flows.",
    checkin: {
      primary: "#22C55E",
      secondary: "#86EFAC",
      background: "#F0FDF4",
      successBackground: "#DCFCE7",
      icon: "#16A34A",
      text: "#14532D",
      earlyCheckIn: {
        background: "#FFF7ED",
        border: "#FDBA74",
        text: "#9A3412",
        icon: "#EA580C"
      },
      lateCheckIn: {
        background: "#FEF2F2",
        border: "#FCA5A5",
        text: "#7F1D1D",
        icon: "#DC2626"
      }
    },
    checkout: {
      primary: "#3B82F6",
      secondary: "#93C5FD",
      background: "#EFF6FF",
      successBackground: "#DBEAFE",
      icon: "#2563EB",
      text: "#1E3A8A",
      lateCheckout: {
        background: "#FFF7ED",
        border: "#FDBA74",
        text: "#9A3412",
        icon: "#EA580C"
      }
    },
    upgrade: {
      primary: "#A855F7",
      secondary: "#C084FC",
      background: "#FAF5FF",
      badge: {
        background: "#F3E8FF",
        text: "#581C87",
        border: "#D8B4FE"
      },
      comparison: {
        current: "#F5F5F5",
        upgraded: "#FAF5FF",
        benefit: "#22C55E"
      }
    }
  },

  events: {
    _description: "Business events theming — for corporate/group booking workflows.",
    business: {
      primary: "#6366F1",
      secondary: "#A5B4FC",
      background: "#EEF2FF",
      border: "#C7D2FE",
      icon: "#4F46E5",
      text: "#312E81"
    },
    indicators: {
      attendees: "#10B981",
      duration: "#3B82F6",
      capacity: "#F59E0B",
      status: "#6366F1"
    }
  },

  changeRequests: {
    _description: "Change request workflow theming — for reservation modifications.",
    primary: "#F59E0B",
    secondary: "#FCD34D",
    background: "#FFFBEB",
    border: "#FDE68A",
    icon: "#D97706",
    text: "#78350F",
    status: {
      pending: "#F59E0B",
      approved: "#22C55E",
      rejected: "#DC2626",
      processing: "#3B82F6"
    }
  },

  transactionalUI: {
    _description: "Tokens for all non-chat screens: reservations, extend stay, check-in, payments, room selection, activity booking, dining, etc.",
    card: {
      background: "#FFFFFF",
      border: "1px solid #E0E0E0",
      borderRadius: "0",
      shadow: "0 4px 12px rgba(76, 13, 22, 0.12)",
      padding: "40px 44px 36px",
      cornerAccent: {
        enabled: true,
        color: "#AC7F42",
        size: "22px",
        offset: "10px",
        borderWidth: "1px"
      }
    },
    sectionHeading: {
      fontFamily: "Playfair Display, Georgia, 'Times New Roman', serif",
      fontSize: "13px",
      fontWeight: "400",
      color: "#4A4A4A",
      textTransform: "uppercase",
      letterSpacing: "0.22em",
      decorativeRule: {
        enabled: true,
        lineWidth: "50px",
        lineColor: "linear-gradient(90deg, transparent, #AC7F42, transparent)",
        diamondSize: "5px",
        diamondColor: "#AC7F42"
      }
    },
    dataLabel: {
      fontFamily: "system-ui, -apple-system, 'Segoe UI', sans-serif",
      fontSize: "9px",
      fontWeight: "500",
      color: "#4A4A4A",
      textTransform: "uppercase",
      letterSpacing: "0.18em"
    },
    dataValue: {
      fontFamily: "Playfair Display, Georgia, 'Times New Roman', serif",
      fontSize: "20px",
      fontWeight: "400",
      color: "#1A1A1A",
      variants: {
        highlighted: {
          color: "#6E5028",
          fontSize: "30px",
          fontWeight: "500"
        },
        emphasis: {
          color: "#4C0D16",
          fontWeight: "500"
        }
      }
    },
    divider: {
      vertical: {
        width: "1px",
        color: "linear-gradient(180deg, transparent, #AC7F42 25%, #AC7F42 75%, transparent)"
      },
      horizontal: {
        width: "1px",
        color: "#F0F0F0"
      },
      accent: {
        width: "60px",
        height: "1px",
        color: "#AC7F42"
      }
    },
    infoBanner: {
      background: "#FDF6F0",
      border: "1px solid #AC7F42",
      borderRadius: "0",
      headingColor: "#1A1A1A",
      bodyColor: "#4A4A4A",
      iconColor: "#AC7F42",
      iconType: "line-art"
    },
    progressStepper: {
      activeStep: {
        textColor: "#FFFFFF",
        background: "#4C0D16",
        borderColor: "#4C0D16"
      },
      completedStep: {
        textColor: "#FFFFFF",
        background: "#AC7F42",
        borderColor: "#AC7F42",
        checkColor: "#FFFFFF"
      },
      pendingStep: {
        textColor: "#4A4A4A",
        background: "transparent",
        borderColor: "#E0E0E0"
      },
      connector: {
        color: "#E0E0E0",
        completedColor: "#AC7F42",
        width: "1px"
      },
      label: {
        fontFamily: "system-ui, -apple-system, 'Segoe UI', sans-serif",
        fontSize: "11px",
        fontWeight: "500",
        color: "#4A4A4A",
        activeColor: "#1A1A1A"
      }
    },
    button: {
      primary: {
        text: "#FFFFFF",
        background: "#4C0D16",
        border: "none",
        hoverBackground: "#3A0A11",
        fontSize: "14px",
        fontWeight: "500",
        padding: "14px 32px",
        borderRadius: "0",
        letterSpacing: "0.08em"
      },
      secondary: {
        text: "#4C0D16",
        background: "transparent",
        border: "1px solid #4C0D16",
        hoverBackground: "#FDF6F0",
        fontSize: "14px",
        fontWeight: "500",
        padding: "14px 32px",
        borderRadius: "0",
        letterSpacing: "0.08em"
      },
      cta: {
        text: "#FFFFFF",
        background: "#AC7F42",
        border: "none",
        hoverBackground: "#8B6634",
        fontSize: "14px",
        fontWeight: "600",
        padding: "14px 32px",
        borderRadius: "0",
        minFontSize: "16px",
        _note: "White on #AC7F42 is 3.58:1 — use min 16px bold to qualify as large text for AA. For smaller text, use dark text variant."
      },
      ctaDarkText: {
        text: "#6E5028",
        background: "#FDF6F0",
        border: "1px solid #AC7F42",
        hoverBackground: "#FAF0E0",
        fontSize: "14px",
        fontWeight: "600"
      }
    },
    memberBadge: {
      text: "#AC7F42",
      background: "transparent",
      border: "1px solid #AC7F42",
      fontSize: "10px",
      fontWeight: "500",
      letterSpacing: "0.12em",
      textTransform: "uppercase",
      padding: "3px 10px"
    },
    reservationCard: {
      image: {
        borderRadius: "0",
        overlay: "linear-gradient(180deg, transparent 60%, rgba(0,0,0,0.4) 100%)"
      },
      stayBadge: {
        text: "#FFFFFF",
        background: "rgba(76, 13, 22, 0.85)",
        fontSize: "10px",
        fontWeight: "500",
        letterSpacing: "0.12em",
        textTransform: "uppercase",
        padding: "6px 12px"
      },
      confirmationNumber: {
        fontFamily: "system-ui, -apple-system, 'Segoe UI', sans-serif",
        fontSize: "11px",
        color: "#4A4A4A",
        letterSpacing: "0.06em"
      },
      dateRange: {
        fontFamily: "Playfair Display, Georgia, 'Times New Roman', serif",
        fontSize: "20px",
        color: "#1A1A1A",
        fontWeight: "400"
      }
    }
  },

  calendar: {
    container: {
      background: "#FFFFFF",
      border: "1px solid #E0E0E0",
      borderRadius: "0",
      padding: "32px 28px",
      shadow: "0 4px 20px rgba(0,0,0,0.06)"
    },
    monthHeading: {
      fontFamily: "Playfair Display, Georgia, 'Times New Roman', serif",
      fontSize: "18px",
      fontWeight: "500",
      color: "#4A4A4A",
      letterSpacing: "0.06em"
    },
    navigation: {
      color: "#4A4A4A",
      hoverColor: "#1A1A1A",
      size: "18px",
      style: "text-only",
      _note: "No filled circles. Text-only chevrons or thin SVG arrows."
    },
    dayOfWeekHeader: {
      fontFamily: "system-ui, -apple-system, 'Segoe UI', sans-serif",
      fontSize: "9px",
      fontWeight: "500",
      color: "#4A4A4A",
      textTransform: "uppercase",
      letterSpacing: "0.15em"
    },
    dayCell: {
      default: {
        fontFamily: "system-ui, -apple-system, 'Segoe UI', sans-serif",
        fontSize: "14px",
        fontWeight: "400",
        color: "#1A1A1A",
        background: "transparent",
        border: "1.5px solid transparent",
        hoverBackground: "#FAF6F0"
      },
      disabled: {
        color: "#736960",
        background: "transparent",
        cursor: "default"
      },
      currentCheckout: {
        color: "#FFFFFF",
        background: "#4C0D16",
        fontWeight: "500",
        hoverBackground: "#3A0A11",
        subLabel: {
          color: "rgba(255,255,255,0.8)",
          fontSize: "7px",
          fontWeight: "500",
          textTransform: "uppercase",
          letterSpacing: "0.1em"
        }
      },
      selected: {
        color: "#6E5028",
        background: "#FFFFFF",
        border: "1.5px solid #AC7F42",
        fontWeight: "600",
        hoverBackground: "#FDF6F0"
      },
      today: {
        indicator: {
          type: "dot",
          size: "4px",
          color: "#AC7F42"
        }
      },
      range: {
        background: "#FDF6F0",
        startEnd: {
          background: "#4C0D16",
          color: "#FFFFFF"
        }
      }
    }
  },

  forms: {
    dateSelector: {
      border: "1px solid #AC7F42",
      background: "#FAFAFA",
      padding: "18px 24px",
      textColor: "#1A1A1A",
      fontFamily: "Playfair Display, Georgia, 'Times New Roman', serif",
      fontSize: "24px",
      fontWeight: "500",
      hover: {
        background: "#FAF6F0",
        borderColor: "#8B6634"
      },
      chevron: {
        color: "#AC7F42",
        position: "right 20px center"
      }
    },
    textInput: {
      border: "1px solid #E0E0E0",
      background: "#FFFFFF",
      focusBorder: "#AC7F42",
      focusShadow: "0 0 0 3px rgba(172, 127, 66, 0.15)",
      textColor: "#1A1A1A",
      placeholderColor: "#6E6E6E",
      fontSize: "15px",
      padding: "14px 16px",
      borderRadius: "0"
    },
    select: {
      border: "1px solid #E0E0E0",
      background: "#FFFFFF",
      textColor: "#1A1A1A",
      chevronColor: "#AC7F42",
      focusBorder: "#AC7F42",
      optionHover: "#FDF6F0"
    },
    checkbox: {
      border: "1px solid #E0E0E0",
      checkedBackground: "#4C0D16",
      checkedBorder: "#4C0D16",
      checkColor: "#FFFFFF",
      size: "20px",
      borderRadius: "2px"
    },
    radio: {
      border: "1px solid #E0E0E0",
      checkedBackground: "#4C0D16",
      checkedBorder: "#4C0D16",
      dotColor: "#FFFFFF",
      size: "20px",
      dotSize: "10px"
    },
    errorStates: {
      border: "#DC2626",
      background: "#FEF2F2",
      text: "#DC2626",
      focusShadow: "0 0 0 3px rgba(220, 38, 38, 0.15)"
    },
    successStates: {
      border: "#22C55E",
      background: "#F0FDF4",
      focusShadow: "0 0 0 3px rgba(34, 197, 94, 0.15)"
    },
    disabledStates: {
      background: "#F9FAFB",
      border: "#E5E7EB",
      text: "#9CA3AF"
    }
  },

  modal: {
    _description: "Tokens for all modal/overlay/dialog patterns — error modals, session expiry, sign-out, workflow confirmations, etc.",
    backdrop: {
      background: "rgba(0, 0, 0, 0.5)"
    },
    container: {
      background: "#FFFFFF",
      border: "1px solid #E0E0E0",
      shadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
      borderRadius: "0",
      padding: "32px",
      maxWidth: "600px"
    },
    header: {
      borderBottom: "1px solid #E0E0E0",
      titleColor: "#1A1A1A",
      titleFontFamily: "Playfair Display, Georgia, 'Times New Roman', serif",
      titleFontSize: "18px",
      titleFontWeight: "500",
      closeButtonColor: "#4A4A4A",
      closeButtonHoverColor: "#1A1A1A"
    },
    body: {
      textColor: "#4A4A4A",
      fontSize: "15px",
      lineHeight: "1.6"
    },
    footer: {
      borderTop: "1px solid #E0E0E0",
      padding: "16px 0 0"
    }
  },

  dropdown: {
    _description: "Tokens for dropdown/select menus, custom dropdowns, and popovers that contain selectable options.",
    container: {
      background: "#FFFFFF",
      border: "1px solid #E0E0E0",
      shadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
      borderRadius: "0",
      maxHeight: "300px"
    },
    option: {
      text: "#1A1A1A",
      hoverBackground: "#FAF6F0",
      selectedBackground: "#FDF6F0",
      selectedText: "#1A1A1A",
      padding: "12px 16px",
      fontSize: "15px"
    },
    divider: "#F0F0F0",
    arrow: {
      color: "#6E6E6E",
      size: "16px"
    }
  },

  link: {
    color: "#AC7F42",
    hoverColor: "#8B6634",
    visitedColor: "#8B6634",
    underline: "none",
    hoverUnderline: "underline",
    fontSize: "inherit",
    fontWeight: "500"
  },

  loading: {
    _description: "Skeleton screens, spinners, and loading overlays.",
    skeleton: {
      background: "#E5E7EB",
      highlight: "#F3F4F6",
      borderRadius: "4px",
      animation: "pulse 1.5s ease-in-out infinite"
    },
    spinner: {
      primary: "#4C0D16",
      secondary: "#AC7F42",
      size: "24px",
      strokeWidth: "2px"
    },
    overlay: {
      background: "rgba(255, 255, 255, 0.8)"
    }
  },

  tooltip: {
    background: "#1A1A1A",
    text: "#FFFFFF",
    border: "none",
    shadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
    fontSize: "13px",
    padding: "8px 12px",
    borderRadius: "4px",
    maxWidth: "300px"
  },

  toggle: {
    backgroundOff: "#E5E7EB",
    backgroundOn: "#4C0D16",
    handle: {
      background: "#FFFFFF",
      size: "20px"
    },
    border: "none",
    width: "44px",
    height: "24px"
  },

  table: {
    header: {
      fontFamily: "system-ui, -apple-system, 'Segoe UI', sans-serif",
      fontSize: "10px",
      fontWeight: "500",
      color: "#4A4A4A",
      textTransform: "uppercase",
      letterSpacing: "0.12em",
      borderBottom: "1px solid #E0E0E0",
      padding: "12px 16px"
    },
    cell: {
      fontFamily: "system-ui, -apple-system, 'Segoe UI', sans-serif",
      fontSize: "14px",
      color: "#1A1A1A",
      padding: "14px 16px",
      borderBottom: "1px solid #F0F0F0"
    },
    rowHover: "#FAF6F0",
    stripe: "transparent"
  },

  pricing: {
    amount: {
      fontFamily: "Playfair Display, Georgia, 'Times New Roman', serif",
      fontSize: "28px",
      fontWeight: "500",
      color: "#1A1A1A"
    },
    currency: {
      fontSize: "16px",
      fontWeight: "400",
      color: "#4A4A4A"
    },
    perUnit: {
      fontSize: "12px",
      color: "#4A4A4A"
    },
    total: {
      fontSize: "32px",
      fontWeight: "600",
      color: "#4C0D16"
    },
    strikethrough: {
      color: "#736960",
      textDecoration: "line-through"
    },
    savings: {
      color: "#2D5A3D",
      background: "#EBF8FF",
      fontSize: "12px",
      fontWeight: "500"
    }
  },

  aiAssistant: {
    name: "Sara",
    logoUrl: "https://objectstorage.us-phoenix-1.oraclecloud.com/n/axfssfqk44re/b/cordiantone-zenpacific/o/2025-07-09/e076152e-e9d2-4883-926a-58c44b4cf14d.png",
    avatar: {
      size: "36px",
      border: "2px solid #AC7F42",
      shadow: "0 4px 12px rgba(172, 127, 66, 0.25)",
      fallback: {
        letter: "C",
        backgroundColor: "#4C0D16"
      },
      gradient: {
        end: "#4C0D16",
        start: "#AC7F42"
      }
    },
    nameStyle: {
      color: "#4C0D16",
      fontSize: "15px",
      fontWeight: "600"
    },
    timestamp: {
      color: "#6E6E6E",
      fontSize: "12px"
    }
  },

  userInterface: {
    topBar: {
      text: "#FFFFFF",
      background: "linear-gradient(135deg, #4C0D16 0%, #8B1538 100%)",
      borderBottom: "#AC7F42"
    },
    inputBox: {
      active: {
        text: "#1A1A1A",
        border: "#AC7F42",
        shadow: "0 0 0 3px rgba(172, 127, 66, 0.15)",
        background: "#FFFFFF"
      },
      inactive: {
        text: "#1A1A1A",
        border: "#E0E0E0",
        background: "#FAFAFA",
        placeholder: "#6E6E6E"
      }
    },
    sendButton: {
      text: "#FFFFFF",
      hover: {
        text: "#FFFFFF",
        border: "#8B6634",
        shadow: "0 4px 12px rgba(172, 127, 66, 0.3)",
        background: "#8B6634"
      },
      border: "#AC7F42",
      shadow: "0 2px 8px rgba(172, 127, 66, 0.2)",
      disabled: {
        border: "#9CA3AF",
        background: "#9CA3AF"
      },
      background: "#AC7F42"
    },
    userMessage: {
      text: "#FFFFFF",
      border: "#E0E0E0",
      shadow: "0 8px 24px rgba(76, 13, 22, 0.08)",
      background: "#4C0D16"
    }
  },

  aiResponseSystem: {
    avatar: {
      size: "36px",
      image: {
        width: "100%",
        height: "100%",
        objectFit: "cover"
      },
      border: "2px solid #AC7F42",
      shadow: "0 4px 12px rgba(172, 127, 66, 0.25)",
      borderRadius: "50%"
    },
    contentTypes: {
      text: {
        color: "#1A1A1A",
        fontSize: "15px",
        lineHeight: "1.6"
      },
      chips: {
        text: "#FFFFFF",
        hover: {
          text: "#FFFFFF",
          border: "#4A4A4A",
          shadow: "0 4px 8px #4A4A4A50",
          background: "#4A4A4ACC"
        },
        border: "#4A4A4A",
        padding: "10px 16px",
        fontSize: "13px",
        minHeight: "44px",
        background: "#4A4A4A",
        borderRadius: "12px"
      },
      image: {
        shadow: "0 8px 24px rgba(76, 13, 22, 0.08)",
        borderRadius: "12px",
        caption: {
          text: "#545454",
          padding: "8px 12px",
          fontSize: "12px",
          borderTop: "#F0F0F0",
          background: "#FAFAFA"
        }
      },
      details: {
        title: {
          text: "#4C0D16",
          padding: "12px 16px",
          fontSize: "14px",
          background: "#FDF6F0",
          fontWeight: "600",
          borderBottom: "#F0D4A3"
        },
        border: "#F0D4A3",
        content: {
          text: "#4A4A4A",
          padding: "16px",
          fontSize: "14px",
          background: "#FFFFFF",
          lineHeight: "1.5"
        },
        background: "#FFFFFF",
        borderRadius: "8px"
      },
      feature: {
        icon: ":star:",
        text: "#4C0D16",
        border: "#F0D4A3",
        padding: "18px 20px",
        background: "linear-gradient(135deg, #FDF6F0 0%, #FEF9F3 100%)",
        borderLeft: "#AC7F42",
        borderRadius: "8px"
      },
      success: {
        icon: ":check:",
        text: "#2D5A3D",
        border: "#BBF7D0",
        padding: "16px 20px",
        background: "linear-gradient(135deg, #EBF8FF 0%, #F0F9FF 100%)",
        borderLeft: "#22C55E",
        borderRadius: "8px"
      },
      warning: {
        icon: ":warning:",
        text: "#744210",
        border: "#F6D55C",
        padding: "16px 20px",
        background: "linear-gradient(135deg, #FEF5E7 0%, #FEFBF3 100%)",
        borderLeft: "#8B5A00",
        borderRadius: "8px"
      },
      highlight: {
        icon: ":four_leaf_clover:",
        text: "#4C0D16",
        border: "#F0D4A3",
        padding: "18px 20px",
        background: "linear-gradient(135deg, #FDF6F0 0%, #FEF9F3 100%)",
        borderLeft: "#AC7F42",
        borderRadius: "8px"
      },
      weather: {
        icon: ":sun:",
        text: "#92400E",
        border: "#FED7AA",
        padding: "16px 20px",
        background: "linear-gradient(135deg, #FFFBEB 0%, #FEF9F3 100%)",
        borderLeft: "#F59E0B",
        borderRadius: "8px"
      }
    },
    responseCard: {
      border: "#F0F0F0",
      shadow: "0 8px 24px rgba(76, 13, 22, 0.08)",
      background: "#FFFFFF",
      borderRadius: "12px"
    }
  }
};
